import React, { useState } from "react";
import { useList, useInvalidate } from "@/lib/hooks";
import api, { apiError } from "@/lib/api";
import { PageHeader, Panel, EmptyState, Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { mediaUrl } from "@/lib/upload";
import { UsersThree, PaperPlaneTilt, CheckCircle, Clock, IdentificationBadge, UserCircle, Copy, Ticket, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";
import { OnboardingDetails } from "@/components/OnboardingDetails";
import { Employee360 } from "@/components/hr/Employee360";
import { SalarySlips, HrAnalytics } from "@/components/hr/SalarySlips";
import { DispatchChecksPanel } from "@/components/hr/DispatchChecksPanel";
import { CheckScorecard } from "@/components/hr/CheckScorecard";

function Stat({ label, value, icon: Icon, tone = "blue" }) {
  const tones = { blue: "bg-blue-50 text-blue-600", emerald: "bg-emerald-50 text-emerald-600", amber: "bg-amber-50 text-amber-600" };
  return (
    <Panel className="p-4 flex items-center gap-3">
      <div className={`w-11 h-11 rounded-xl grid place-items-center ${tones[tone]}`}><Icon size={22} weight="duotone" /></div>
      <div><p className="text-2xl font-heading font-extrabold text-slate-900 tabular">{value}</p><p className="text-xs text-slate-500">{label}</p></div>
    </Panel>
  );
}

export default function HR() {
  const [viewing360, setViewing360] = useState(null);
  const { data: overview, isLoading } = useList("hr-overview", "/hr/overview");
  const { data: invites = [] } = useList("hr-onboarding", "/hr/onboarding");
  const { data: tickets = [] } = useList("hr-tickets", "/tickets", { refetchInterval: 20000 });
  const invalidate = useInvalidate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "sales" });
  const [sending, setSending] = useState(false);
  const [reply, setReply] = useState(null); // {id, subject, admin_response}
  const [viewing, setViewing] = useState(null);

  const emps = overview?.employees || [];

  const setTicketStatus = async (id, status) => {
    try { await api.put(`/tickets/${id}`, { status }); invalidate(["hr-tickets"]); toast.success(`Ticket ${status}`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveReply = async () => {
    try {
      await api.put(`/tickets/${reply.id}`, { admin_response: reply.admin_response });
      invalidate(["hr-tickets"]); setReply(null); toast.success("Response sent");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const send = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email are required"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/hr/onboarding", { ...form, base_url: window.location.origin });
      if (data.email_sent) toast.success("Onboarding link emailed"); else if (data.email_configured) toast.warning("Invite created, but the email failed to send — share the link manually"); else toast.success("Invite created (email not configured)");
      setOpen(false); setForm({ name: "", email: "", role: "sales" }); invalidate("hr-onboarding");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const copyLink = (token) => {
    navigator.clipboard?.writeText(`${window.location.origin}/onboarding/${token}`);
    toast.success("Link copied");
  };

  const inp = "bg-white border-slate-300";

  return (
    <div>
      <PageHeader title="HR Operations" subtitle="Team roster, attendance and employee onboarding"
        actions={<Button data-testid="send-onboarding-btn" onClick={() => setOpen(true)} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold"><PaperPlaneTilt size={17} weight="fill" className="mr-1.5" />Send Onboarding Link</Button>} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <Stat label="Employees" value={overview?.total_employees ?? "—"} icon={UsersThree} />
        <Stat label="Present Today" value={overview?.present_today ?? "—"} icon={CheckCircle} tone="emerald" />
        <Stat label="Sales Agents" value={overview?.by_role?.sales ?? "—"} icon={UserCircle} />
        <Stat label="Dispatchers" value={overview?.by_role?.dispatcher ?? "—"} icon={IdentificationBadge} tone="amber" />
      </div>

      <HrAnalytics />
      <DispatchChecksPanel />
      <CheckScorecard />
      <Tabs defaultValue="roster">
        <TabsList className="bg-white border border-slate-200 mb-4">
          <TabsTrigger value="roster" data-testid="tab-roster"><UsersThree size={16} className="mr-1.5" />Roster & Attendance</TabsTrigger>
          <TabsTrigger value="slips" data-testid="tab-slips">Salary Slips</TabsTrigger>
          <TabsTrigger value="onboarding" data-testid="tab-onboarding"><IdentificationBadge size={16} className="mr-1.5" />Onboarding</TabsTrigger>
          <TabsTrigger value="tickets" data-testid="tab-tickets"><Ticket size={16} className="mr-1.5" />Tickets{tickets.filter((t) => t.status === "open").length > 0 && <span className="ml-1.5 bg-red-500 text-white text-[10px] font-bold px-1.5 rounded-full">{tickets.filter((t) => t.status === "open").length}</span>}</TabsTrigger>
        </TabsList>

        <TabsContent value="roster">
          <Panel className="p-0">
            {isLoading ? <p className="p-6 text-slate-400">Loading…</p> : emps.length === 0 ? (
              <EmptyState icon={UsersThree} title="No employees" hint="Invite your team with an onboarding link." />
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-200 hover:bg-transparent">
                      <TableHead>Employee</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead className="hidden md:table-cell">Today</TableHead>
                      <TableHead className="hidden lg:table-cell">Days Present</TableHead>
                      <TableHead className="hidden lg:table-cell">Activity</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {emps.map((e) => (
                      <TableRow key={e.id} className="border-slate-100 cursor-pointer hover:bg-slate-50" onClick={() => setViewing360(e.id)} data-testid={`hr-emp-${e.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-blue-100 overflow-hidden grid place-items-center text-sm font-bold text-blue-700">
                              {e.avatar_url ? <img src={mediaUrl(e.avatar_url)} alt="" className="w-full h-full object-cover" /> : e.name?.[0]?.toUpperCase()}
                            </div>
                            <div><p className="font-medium text-slate-900">{e.name}</p><p className="text-xs text-slate-500">{e.email}</p></div>
                          </div>
                        </TableCell>
                        <TableCell><span className="capitalize text-sm text-slate-700">{e.role}</span>{e.title && <p className="text-xs text-slate-400">{e.title}</p>}</TableCell>
                        <TableCell className="hidden md:table-cell">
                          {e.checked_in_today ? (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />In {e.check_in ? new Date(e.check_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}</span>
                          ) : <span className="inline-flex items-center gap-1 text-xs text-slate-400"><Clock size={12} />Not in</span>}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell font-mono text-sm text-slate-600">{e.present_days}</TableCell>
                        <TableCell className="hidden lg:table-cell text-sm text-slate-600">{e.role === "sales" ? `${e.leads} leads` : e.role === "dispatcher" ? `${e.loads} loads` : "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Panel>
          <p className="text-xs text-slate-400 mt-2">Click an employee to open their full 360° profile — performance, attendance, reviews, tickets and salary.</p>
        </TabsContent>
        <TabsContent value="slips"><SalarySlips employees={emps} /></TabsContent>
        <Employee360 uid={viewing360} onClose={() => setViewing360(null)} />

        <TabsContent value="onboarding">
          <Panel className="p-0">
            {invites.length === 0 ? (
              <EmptyState icon={IdentificationBadge} title="No onboarding invites yet" hint="Send an onboarding link to a new hire to collect their details and documents." />
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-200 hover:bg-transparent">
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invites.map((iv) => (
                      <TableRow key={iv.id} className="border-slate-100" data-testid={`hr-invite-${iv.id}`}>
                        <TableCell className="font-medium text-slate-900">{iv.name}</TableCell>
                        <TableCell className="text-sm text-slate-600">{iv.email}</TableCell>
                        <TableCell className="capitalize text-sm text-slate-700">{iv.role}</TableCell>
                        <TableCell>
                          {iv.submitted
                            ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full"><CheckCircle size={12} weight="fill" />Completed</span>
                            : <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full"><Clock size={12} />Pending</span>}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end items-center gap-3">
                            {iv.submitted && <Button onClick={() => setViewing(iv)} className="h-8 bg-blue-600 text-white hover:bg-blue-700 text-xs font-semibold" data-testid={`view-onboarding-${iv.id}`}><Eye size={14} className="mr-1" />View Details</Button>}
                            <button onClick={() => copyLink(iv.token)} className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-semibold" data-testid={`copy-link-${iv.id}`}><Copy size={14} />Copy Link</button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Panel>
        </TabsContent>

        <TabsContent value="tickets">
          <Panel className="p-0">
            {tickets.length === 0 ? (
              <EmptyState icon={Ticket} title="No tickets" hint="Leave, appraisal and concern tickets from your team appear here." />
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-200 hover:bg-transparent">
                      <TableHead>Employee</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Subject</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tickets.map((tk) => (
                      <TableRow key={tk.id} className="border-slate-100" data-testid={`hr-ticket-${tk.id}`}>
                        <TableCell><p className="font-medium text-slate-900">{tk.user_name}</p><p className="text-xs text-slate-400 capitalize">{tk.user_role}</p></TableCell>
                        <TableCell className="capitalize text-sm text-slate-700">{tk.type}</TableCell>
                        <TableCell>
                          <p className="text-sm text-slate-900">{tk.subject}</p>
                          {tk.start_date && <p className="text-xs text-slate-400">{tk.start_date}{tk.end_date ? ` → ${tk.end_date}` : ""}</p>}
                          {tk.description && <p className="text-xs text-slate-500 mt-0.5 max-w-xs">{tk.description}</p>}
                          {tk.admin_response && <p className="text-xs text-blue-600 mt-0.5">↳ {tk.admin_response}</p>}
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${tk.status === "open" ? "bg-amber-50 text-amber-700" : tk.status === "approved" ? "bg-emerald-50 text-emerald-700" : tk.status === "rejected" ? "bg-red-50 text-red-700" : "bg-blue-50 text-blue-700"}`}>{tk.status}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1.5">
                            <Select value={tk.status} onValueChange={(v) => setTicketStatus(tk.id, v)}>
                              <SelectTrigger className="h-8 w-28 bg-white border-slate-300 text-xs" data-testid={`ticket-status-${tk.id}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="open">Open</SelectItem>
                                <SelectItem value="approved">Approved</SelectItem>
                                <SelectItem value="rejected">Rejected</SelectItem>
                                <SelectItem value="resolved">Resolved</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button variant="outline" onClick={() => setReply({ id: tk.id, subject: tk.subject, admin_response: tk.admin_response || "" })} className="h-8 border-slate-300 bg-white text-slate-700 text-xs" data-testid={`ticket-reply-${tk.id}`}>Reply</Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Panel>
        </TabsContent>
      </Tabs>

      <OnboardingDetails invite={viewing} onClose={() => setViewing(null)} />

      <Dialog open={!!reply} onOpenChange={(v) => !v && setReply(null)}>
        <DialogContent className="bg-white border-slate-200 max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Respond to Ticket</DialogTitle><DialogDescription className="sr-only">Send a response to the employee</DialogDescription></DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">{reply?.subject}</p>
          <div className="py-2">
            <Field label="Your Response"><Input data-testid="ticket-response-input" value={reply?.admin_response || ""} onChange={(e) => setReply({ ...reply, admin_response: e.target.value })} className="bg-white border-slate-300" placeholder="Message to the employee" /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReply(null)} className="border-slate-300 bg-white text-slate-700">Cancel</Button>
            <Button onClick={saveReply} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold" data-testid="ticket-response-save">Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Send Onboarding Link</DialogTitle><DialogDescription className="sr-only">Invite a new hire by email</DialogDescription></DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">We'll email the new hire a secure link to complete their onboarding form and upload documents.</p>
          <div className="space-y-4 py-2">
            <Field label="Full Name *"><Input data-testid="onboard-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} /></Field>
            <Field label="Email *"><Input data-testid="onboard-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inp} /></Field>
            <Field label="Role">
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className={inp} data-testid="onboard-role"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="sales">Sales Agent</SelectItem><SelectItem value="dispatcher">Dispatcher</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
              </Select>
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-300 bg-white text-slate-700">Cancel</Button>
            <Button onClick={send} disabled={sending} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold" data-testid="onboard-send-btn">{sending ? "Sending…" : "Send Link"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
